/**
 * Assignments.tsx — Per-chatter creator assignment editor (D-08/D-09).
 *
 * One row per chatter. Each chatter has a "Manage creators" toggle that reveals
 * the full list of every creator (maloum_account) in the CRM as a checklist:
 *   - tick a creator   → POST /assignments   (create a standing assignment)
 *   - untick a creator → DELETE /assignments/:id (soft-close the assignment)
 *
 * Data sources:
 *   - GET /chatters    → the operator list (role = chatter)
 *   - GET /accounts    → every creator in the CRM
 *   - GET /assignments → active (open-ended) chatter→creator assignments
 *
 * Explicitly NO shift fields, NO rota, NO coverage-gap view (D-09).
 */

import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignmentsApi,
  accountsApi,
  chattersApi,
  ApiRequestError,
  type AssignmentRow,
  type ChatterDto,
} from '../lib/api-client.js';
import type { AccountDto } from '@crm/types';
import {
  Button,
  Input,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  useToast,
} from '@crm/ui';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Best human label for a creator/account. */
function creatorLabel(a: AccountDto): string {
  return a.displayLabel || a.creatorName || `${a.id.slice(0, 8)}…`;
}

/** Does this creator match the free-text search? */
function matchesQuery(a: AccountDto, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    a.displayLabel.toLowerCase().includes(needle) ||
    a.creatorName.toLowerCase().includes(needle) ||
    a.email.toLowerCase().includes(needle) ||
    a.id.toLowerCase().includes(needle)
  );
}

/** Collapsed summary shows at most this many creator chips before "+N more". */
const MAX_SUMMARY_CHIPS = 8;

// ---------------------------------------------------------------------------
// Per-chatter card
// ---------------------------------------------------------------------------

interface ChatterCardProps {
  chatter: ChatterDto;
  creators: AccountDto[];
  /** accountId → assignmentId for THIS chatter's active assignments */
  assigned: Map<string, string>;
  /** Returns true while a toggle for (this chatter, accountId) is in flight. */
  isPending: (accountId: string) => boolean;
  /** Toggle assignment. assignmentId present → unassign; absent → assign. */
  onToggle: (
    accountId: string,
    assignmentId: string | undefined,
    label: string,
  ) => void;
}

function ChatterCard({
  chatter,
  creators,
  assigned,
  isPending,
  onToggle,
}: ChatterCardProps) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');

  // The collapsed-branch buttons ("Assign creators" / "+N more") unmount themselves
  // when they open the checklist — without a focus handoff, keyboard focus falls to
  // <body> and the next Tab restarts from the top of the page (WCAG 2.4.3).
  const focusSearchRef = React.useRef(false);
  const searchWrapRef = React.useRef<HTMLDivElement>(null);
  const openAndFocusSearch = () => {
    focusSearchRef.current = true;
    setOpen(true);
  };
  React.useEffect(() => {
    if (open && focusSearchRef.current) {
      focusSearchRef.current = false;
      searchWrapRef.current?.querySelector('input')?.focus();
    }
  }, [open]);

  const assignedCount = assigned.size;
  const filtered = React.useMemo(
    () => creators.filter((c) => matchesQuery(c, q)),
    [creators, q],
  );

  // Assigned creators (for the collapsed summary chips)
  const assignedCreators = React.useMemo(
    () => creators.filter((c) => assigned.has(c.id)),
    [creators, assigned],
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-text truncate">{chatter.displayName || chatter.email}</span>
              {chatter.displayName && (
                <span className="text-xs text-text-muted truncate">{chatter.email}</span>
              )}
            </div>
            <Badge variant="default">Chatter</Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? 'Done' : 'Manage creators'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className={open ? 'pt-0' : 'pt-3'}>
        {!open && (
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-text-subtle">
              Assigned creators · {assignedCount}
            </span>
            {assignedCreators.length === 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-subtle">None yet</span>
                <Button variant="ghost" size="sm" onClick={openAndFocusSearch}>
                  Assign creators
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 items-center">
                {assignedCreators.slice(0, MAX_SUMMARY_CHIPS).map((c) => (
                  <Badge key={c.id} variant="outline" className="max-w-[160px]">
                    <span className="truncate">{creatorLabel(c)}</span>
                  </Badge>
                ))}
                {assignedCreators.length > MAX_SUMMARY_CHIPS && (
                  <button
                    type="button"
                    onClick={openAndFocusSearch}
                    className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded leading-none bg-transparent text-text-muted border border-border hover:bg-bg-hover focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none"
                  >
                    +{assignedCreators.length - MAX_SUMMARY_CHIPS} more
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {open && (
          <div className="flex flex-col gap-3">
            <div ref={searchWrapRef}>
              <Input
                placeholder="Search creators by name, email, or ID…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label={`Filter creators for ${chatter.email}`}
              />
            </div>

            {filtered.length === 0 ? (
              <p className="text-xs text-text-subtle py-2">
                {creators.length === 0
                  ? 'No creators in the CRM yet.'
                  : 'No creators match your search.'}
              </p>
            ) : (
              <ul className="flex flex-col max-h-80 overflow-auto rounded border border-border divide-y divide-border">
                {filtered.map((c) => {
                  const assignmentId = assigned.get(c.id);
                  const checked = assignmentId !== undefined;
                  const pending = isPending(c.id);
                  // Optimistic display while the toggle is in flight.
                  const displayChecked = pending ? !checked : checked;
                  return (
                    <li key={c.id}>
                      <label
                        className={[
                          'flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors',
                          'hover:bg-bg-hover',
                          pending ? 'opacity-60 cursor-wait' : '',
                        ].join(' ')}
                      >
                        <input
                          type="checkbox"
                          checked={displayChecked}
                          disabled={pending}
                          onChange={() =>
                            onToggle(c.id, assignmentId, creatorLabel(c))
                          }
                          className="h-4 w-4 accent-accent rounded focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm text-text truncate">
                            {creatorLabel(c)}
                          </span>
                          <span className="text-xs text-text-subtle truncate">
                            {c.email}
                          </span>
                        </div>
                        {checked && !pending && (
                          <Badge variant="success" className="ml-auto">
                            Assigned
                          </Badge>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function Assignments() {
  const { addToast } = useToast();
  const qc = useQueryClient();

  const chattersQ = useQuery({
    queryKey: ['chatters'],
    queryFn: () => chattersApi.list(),
  });
  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });
  const assignmentsQ = useQuery({
    queryKey: ['assignments'],
    queryFn: () => assignmentsApi.list(),
    refetchInterval: 60_000,
  });

  // accountId → assignmentId, grouped by chatterId (active assignments only).
  const byChatter = React.useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    for (const a of assignmentsQ.data ?? ([] as AssignmentRow[])) {
      if (a.effectiveTo !== null) continue;
      let inner = map.get(a.chatterId);
      if (!inner) {
        inner = new Map();
        map.set(a.chatterId, inner);
      }
      inner.set(a.accountId, a.id);
    }
    return map;
  }, [assignmentsQ.data]);

  // Sorted creator list (stable across renders for the checklist).
  const creators = React.useMemo(
    () =>
      [...(accountsQ.data ?? [])].sort((x, y) =>
        creatorLabel(x).localeCompare(creatorLabel(y)),
      ),
    [accountsQ.data],
  );

  // Per-row pending tracking, keyed `${chatterId}::${accountId}`.
  const [pending, setPending] = React.useState<Set<string>>(
    () => new Set<string>(),
  );
  const keyOf = (chatterId: string, accountId: string) =>
    `${chatterId}::${accountId}`;

  const toggle = React.useCallback(
    async (
      chatterId: string,
      accountId: string,
      assignmentId: string | undefined,
      label: string,
    ) => {
      const key = keyOf(chatterId, accountId);
      setPending((p) => {
        if (p.has(key)) return p;
        const next = new Set(p);
        next.add(key);
        return next;
      });
      try {
        if (assignmentId) {
          await assignmentsApi.delete(assignmentId);
          addToast(`Unassigned ${label}.`, 'success');
        } else {
          await assignmentsApi.create({ chatterId, accountId });
          addToast(`Assigned ${label}.`, 'success');
        }
        // Await the refetch so the checkbox reflects server truth before we
        // clear the pending (optimistic) state — no check→uncheck→check flicker.
        await qc.invalidateQueries({ queryKey: ['assignments'] });
      } catch (err) {
        const msg =
          err instanceof ApiRequestError ? err.message : 'Update failed';
        addToast(msg, 'danger');
      } finally {
        setPending((p) => {
          const next = new Set(p);
          next.delete(key);
          return next;
        });
      }
    },
    [addToast, qc],
  );

  const isLoading =
    chattersQ.isLoading || accountsQ.isLoading || assignmentsQ.isLoading;
  const error = chattersQ.error || accountsQ.error || assignmentsQ.error;
  const chatters = chattersQ.data ?? [];

  return (
    <div className="max-w-4xl mx-auto flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold text-text">Assignments</h1>
        <p className="text-sm text-text-muted mt-0.5">
          Assign creators to each chatter. Open a chatter and tick the creators
          they should cover; untick to remove (no shift scheduling — D-09).
        </p>
      </div>

      {isLoading && (
        <div className="p-4 text-sm text-text-muted">Loading…</div>
      )}

      {error && (
        <div className="p-4 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && chatters.length === 0 && (
        <div className="p-4 text-sm text-text-muted">
          No chatters yet. Add chatter operators before assigning creators.
        </div>
      )}

      {!isLoading &&
        !error &&
        chatters.map((ch) => (
          <ChatterCard
            key={ch.id}
            chatter={ch}
            creators={creators}
            assigned={byChatter.get(ch.id) ?? new Map()}
            isPending={(accountId) => pending.has(keyOf(ch.id, accountId))}
            onToggle={(accountId, assignmentId, label) =>
              toggle(ch.id, accountId, assignmentId, label)
            }
          />
        ))}
    </div>
  );
}
